<template>
  <Dialog
    v-model:visible="visibleLocal"
    :modal="true"
    :draggable="false"
    :closable="true"
    :style="{ width: '760px', maxWidth: '94vw' }"
    class="event-picker-dialog"
    @show="onShow"
  >
    <template #header>
      <div class="ep-header">
        <i class="fa-light fa-bolt" />
        <span>{{ t('eventPicker.title') }}</span>
      </div>
    </template>

    <div class="ep-shell">
      <!-- ============ LEFT RAIL — category + integration filters ============ -->
      <aside class="ep-rail">
        <div class="ep-rail__group">
          <Button
            type="button"
            class="ep-rail__item"
            :class="{ 'is-active': activeFilter.kind === 'all' }"
            @click="setFilter({ kind: 'all' })"
          >
            <span class="ep-rail__icon ep-rail__icon--all">
              <i class="fa-light fa-list" />
            </span>
            <span class="ep-rail__label">{{ t('eventPicker.filters.all') }}</span>
            <span class="ep-rail__count">{{ totalEventCount }}</span>
          </Button>

          <Button
            v-if="predefinedCount > 0"
            type="button"
            class="ep-rail__item"
            :class="{ 'is-active': activeFilter.kind === 'predefined' }"
            @click="setFilter({ kind: 'predefined' })"
          >
            <span class="ep-rail__icon ep-rail__icon--predefined">
              <i class="fa-light fa-bolt" />
            </span>
            <span class="ep-rail__label">{{ t('eventPicker.filters.predefined') }}</span>
            <span class="ep-rail__count">{{ predefinedCount }}</span>
          </Button>

          <Button
            v-if="customCount > 0"
            type="button"
            class="ep-rail__item"
            :class="{ 'is-active': activeFilter.kind === 'custom' }"
            @click="setFilter({ kind: 'custom' })"
          >
            <span class="ep-rail__icon ep-rail__icon--custom">
              <i class="fa-light fa-puzzle-piece" />
            </span>
            <span class="ep-rail__label">{{ t('eventPicker.filters.custom') }}</span>
            <span class="ep-rail__count">{{ customCount }}</span>
          </Button>

          <Button
            v-if="integrationTotalCount > 0"
            type="button"
            class="ep-rail__item"
            :class="{ 'is-active': activeFilter.kind === 'integrations-all' }"
            @click="setFilter({ kind: 'integrations-all' })"
          >
            <span class="ep-rail__icon ep-rail__icon--integration">
              <i class="fa-light fa-plug" />
            </span>
            <span class="ep-rail__label">{{ t('eventPicker.filters.integrations') }}</span>
            <span class="ep-rail__count">{{ integrationTotalCount }}</span>
          </Button>
        </div>

        <!-- Per-integration sub-list, indented under "Integrations". -->
        <div
          v-if="integrationBuckets.length > 0"
          class="ep-rail__group ep-rail__group--integrations"
        >
          <div class="ep-rail__subheader">
            {{ t('eventPicker.filters.byIntegration') }}
          </div>
          <Button
            v-for="bucket in integrationBuckets"
            :key="bucket.idIntegration"
            type="button"
            class="ep-rail__item ep-rail__item--nested"
            :class="{
              'is-active':
                activeFilter.kind === 'integration'
                && activeFilter.idIntegration === bucket.idIntegration,
            }"
            @click="setFilter({ kind: 'integration', idIntegration: bucket.idIntegration })"
          >
            <span class="ep-rail__avatar">
              <img
                v-if="bucket.iconUrl"
                :src="bucket.iconUrl"
                :alt="bucket.name"
              >
              <i
                v-else
                class="fa-solid fa-plug"
              />
            </span>
            <span class="ep-rail__label">{{ bucket.name }}</span>
            <span class="ep-rail__count">{{ bucket.items.length }}</span>
          </Button>
        </div>
      </aside>

      <!-- ============ RIGHT PANE — search + flat list ============ -->
      <main class="ep-pane">
        <div class="ep-pane__search">
          <span class="p-input-icon-left w-full">
            <i class="fa-light fa-search" />
            <InputText
              ref="searchInputRef"
              v-model="searchQuery"
              :placeholder="t('eventPicker.searchPlaceholder')"
              class="w-full"
            />
          </span>
        </div>

        <div class="ep-pane__list">
          <div
            v-if="visibleItems.length === 0"
            class="ep-empty"
          >
            <i class="fa-light fa-search" />
            <span>{{ t('eventPicker.noResults') }}</span>
          </div>

          <Button
            v-for="item in visibleItems"
            :key="item.value"
            type="button"
            class="ep-row"
            :class="{ 'is-selected': item.value === modelValue }"
            @click="selectEvent(item.value)"
          >
            <span class="ep-row__main">
              <span class="ep-row__label">{{ item.label }}</span>
              <span class="ep-row__code">{{ item.value }}</span>
            </span>
            <span class="ep-row__meta">
              <!-- Owner integration chip — only on items that have one.
                   This is the only marker we keep on the row: it carries
                   the integration's logo + name, which the left rail's
                   active filter cannot convey. Predefined / custom items
                   need no marker because they look the same anyway and
                   the active filter already labels them above. -->
              <span
                v-if="item.owner_integration"
                v-tooltip.top="item.owner_integration.name"
                class="ep-row__owner"
              >
                <span class="ep-row__owner-icon">
                  <img
                    v-if="item.owner_integration.icon_url"
                    :src="item.owner_integration.icon_url"
                    :alt="item.owner_integration.name"
                  >
                  <i
                    v-else
                    class="fa-solid fa-plug"
                  />
                </span>
                <span class="ep-row__owner-name">{{ item.owner_integration.name }}</span>
              </span>
            </span>
          </Button>
        </div>
      </main>
    </div>

    <template #footer>
      <div class="ep-footer">
        <Button
          :label="t('eventPicker.cancel')"
          text
          class="p-button-secondary"
          @click="visibleLocal = false"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, PropType, computed, ref, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import type { OwnerIntegrationInfo } from '@/types/integrations';

export interface EventPickerItem {
  label: string;
  value: string;
  owner_integration?: OwnerIntegrationInfo | null;
}

/**
 * Per-integration bucket used for the left-rail nested filter.
 */
interface IntegrationBucket {
  idIntegration: number;
  name: string;
  iconUrl: string | null;
  items: EventPickerItem[];
}

/**
 * The current selection in the left rail. `kind` discriminates:
 *   - 'all'              → show everything
 *   - 'predefined'       → only ShopiMind natives
 *   - 'custom'           → only owner-less customs
 *   - 'integrations-all' → all integration-owned events (any integration)
 *   - 'integration'      → events of a specific integration (idIntegration)
 */
type ActiveFilter =
  | { kind: 'all' }
  | { kind: 'predefined' }
  | { kind: 'custom' }
  | { kind: 'integrations-all' }
  | { kind: 'integration'; idIntegration: number };

export default defineComponent({
  name: 'EventPickerDialog',

  components: {
    Dialog,
    InputText,
    Button,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: String,
      default: '',
    },

    predefinedItems: {
      type: Array as PropType<EventPickerItem[]>,
      default: () => [],
    },

    customItems: {
      type: Array as PropType<EventPickerItem[]>,
      default: () => [],
    },
  },

  emits: ['update:visible', 'update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const searchQuery = ref('');
    const searchInputRef = ref();
    const activeFilter = ref<ActiveFilter>({ kind: 'all' });

    const visibleLocal = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    // ----------------------------------------------------------------------
    // Bucketise customItems once per source change. The left rail's per-
    // integration sub-list and the counts both read from this.
    // ----------------------------------------------------------------------

    const integrationBuckets = computed<IntegrationBucket[]>(() => {
      const map = new Map<number, IntegrationBucket>();
      for (const item of props.customItems) {
        const o = item.owner_integration;
        if (!o?.id_integration) continue;
        let bucket = map.get(o.id_integration);
        if (!bucket) {
          bucket = {
            idIntegration: o.id_integration,
            name: o.name || o.slug || `Integration ${o.id_integration}`,
            iconUrl: o.icon_url ?? null,
            items: [],
          };
          map.set(o.id_integration, bucket);
        }
        bucket.items.push(item);
      }
      // Stable order by name.
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    });

    const customOnly = computed(() => props.customItems.filter(
      (i) => !i.owner_integration?.id_integration,
    ));
    const integrationOnly = computed(() => props.customItems.filter(
      (i) => !!i.owner_integration?.id_integration,
    ));

    const predefinedCount = computed(() => props.predefinedItems.length);
    const customCount = computed(() => customOnly.value.length);
    const integrationTotalCount = computed(() => integrationOnly.value.length);
    const totalEventCount = computed(
      () => predefinedCount.value + customCount.value + integrationTotalCount.value,
    );

    // ----------------------------------------------------------------------
    // The flat list shown on the right, derived from the active filter +
    // the search query.
    // ----------------------------------------------------------------------

    const filteredBase = computed<EventPickerItem[]>(() => {
      const f = activeFilter.value;
      switch (f.kind) {
        case 'all':
          // Order in "All": predefined first (most common), then by
          // integration alphabetical, then customs. Matches the
          // discoverability hierarchy.
          return [
            ...props.predefinedItems,
            ...integrationOnly.value,
            ...customOnly.value,
          ];
        case 'predefined':
          return props.predefinedItems;
        case 'custom':
          return customOnly.value;
        case 'integrations-all':
          return integrationOnly.value;
        case 'integration': {
          const bucket = integrationBuckets.value.find(
            (b) => b.idIntegration === f.idIntegration,
          );
          return bucket?.items ?? [];
        }
        default:
          return [];
      }
    });

    const visibleItems = computed<EventPickerItem[]>(() => {
      const q = searchQuery.value.toLowerCase().trim();
      if (!q) return filteredBase.value;
      return filteredBase.value.filter(
        (item) => item.label.toLowerCase().includes(q)
          || item.value.toLowerCase().includes(q),
      );
    });

    // ----------------------------------------------------------------------
    // Behaviour
    // ----------------------------------------------------------------------

    const setFilter = (next: ActiveFilter) => {
      activeFilter.value = next;
    };

    const onShow = () => {
      searchQuery.value = '';
      activeFilter.value = { kind: 'all' };
      nextTick(() => {
        searchInputRef.value?.$el?.focus();
      });
    };

    const selectEvent = (value: string) => {
      emit('update:modelValue', value);
      visibleLocal.value = false;
    };

    return {
      t,
      visibleLocal,
      searchQuery,
      searchInputRef,
      activeFilter,
      setFilter,
      integrationBuckets,
      predefinedCount,
      customCount,
      integrationTotalCount,
      totalEventCount,
      visibleItems,
      onShow,
      selectEvent,
    };
  },
});
</script>

<style>
/* ============================================================
   Header
   ============================================================ */
.event-picker-dialog .ep-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}
.event-picker-dialog .ep-header i {
  color: #6366f1;
  font-size: 1.1rem;
}

/* ============================================================
   Two-pane shell
   ============================================================ */
.event-picker-dialog .ep-shell {
  display: flex;
  gap: 0;
  height: 460px;
  max-height: 60vh;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}

/* ============================================================
   Left rail
   ============================================================ */
.event-picker-dialog .ep-rail {
  width: 220px;
  flex-shrink: 0;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  padding: 0.75rem 0.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.event-picker-dialog .ep-rail__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.event-picker-dialog .ep-rail__group--integrations {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.75rem;
}

.event-picker-dialog .ep-rail__subheader {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  padding: 0.25rem 0.5rem 0.4rem;
}

.event-picker-dialog .ep-rail__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.55rem;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  text-align: left;
  transition: background 0.1s ease, color 0.1s ease;
  width: 100%;
}

.event-picker-dialog .ep-rail__item:hover {
  background: rgba(99, 102, 241, 0.07);
  color: #1f2937;
}

.event-picker-dialog .ep-rail__item.is-active {
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
  font-weight: 600;
}

.event-picker-dialog .ep-rail__item--nested {
  padding-left: 0.45rem;
}

/* Plain category icons (All / Predefined / Custom / Integrations). */
.event-picker-dialog .ep-rail__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 0.75rem;
  flex-shrink: 0;
}
.event-picker-dialog .ep-rail__icon--all {
  background: #e5e7eb;
  color: #4b5563;
}
.event-picker-dialog .ep-rail__icon--predefined {
  background: #e0e7ff;
  color: #4f46e5;
}
.event-picker-dialog .ep-rail__icon--custom {
  background: #ede9fe;
  color: #7c3aed;
}
.event-picker-dialog .ep-rail__icon--integration {
  background: #f3e8ff;
  color: #6b21a8;
}

/* Per-integration avatar in the sub-list. */
.event-picker-dialog .ep-rail__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1.5px solid #c4b5fd;
  flex-shrink: 0;
  overflow: hidden;
}
.event-picker-dialog .ep-rail__avatar img {
  width: 14px;
  height: 14px;
  object-fit: contain;
}
.event-picker-dialog .ep-rail__avatar i {
  font-size: 0.65rem;
  color: #7c3aed;
}

.event-picker-dialog .ep-rail__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-picker-dialog .ep-rail__count {
  font-size: 0.65rem;
  font-weight: 600;
  background: #e5e7eb;
  color: #6b7280;
  padding: 1px 7px;
  border-radius: 9999px;
  flex-shrink: 0;
}

.event-picker-dialog .ep-rail__item.is-active .ep-rail__count {
  background: #c7d2fe;
  color: #4338ca;
}

/* ============================================================
   Right pane — search + list
   ============================================================ */
.event-picker-dialog .ep-pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.event-picker-dialog .ep-pane__search {
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.event-picker-dialog .ep-pane__search .p-input-icon-left i {
  top: 50% !important;
  transform: translateY(-50%);
  margin-top: 0 !important;
  color: #9ca3af;
  font-size: 0.85rem;
}

.event-picker-dialog .ep-pane__list {
  flex: 1;
  overflow-y: auto;
  padding: 0.4rem 0.4rem 0.6rem;
}

/* Rows are buttons for keyboard navigation. */
.event-picker-dialog .ep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.7rem;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease, border-color 0.1s ease;
}

.event-picker-dialog .ep-row:hover {
  background: #eef2ff;
  border-color: #e0e7ff;
}

.event-picker-dialog .ep-row.is-selected {
  background: #e0e7ff;
  border-color: #c7d2fe;
}

.event-picker-dialog .ep-row__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.event-picker-dialog .ep-row__label {
  font-size: 0.83rem;
  font-weight: 600;
  color: #1f2937;
}

.event-picker-dialog .ep-row__code {
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-picker-dialog .ep-row__meta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}

/* Owner-integration chip on each row. */
.event-picker-dialog .ep-row__owner {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 2px 8px 2px 3px;
  border-radius: 9999px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #5b21b6;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.3;
  max-width: 160px;
}

.event-picker-dialog .ep-row__owner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  background: #ffffff;
  flex-shrink: 0;
  overflow: hidden;
}

.event-picker-dialog .ep-row__owner-icon img {
  width: 10px;
  height: 10px;
  object-fit: contain;
}

.event-picker-dialog .ep-row__owner-icon i {
  font-size: 0.55rem;
  color: #7c3aed;
}

.event-picker-dialog .ep-row__owner-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}

/* ============================================================
   Empty state
   ============================================================ */
.event-picker-dialog .ep-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: #9ca3af;
  gap: 8px;
}
.event-picker-dialog .ep-empty i {
  font-size: 2rem;
}
.event-picker-dialog .ep-empty span {
  font-size: 0.85rem;
}

/* ============================================================
   Footer
   ============================================================ */
.event-picker-dialog .ep-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
